import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '../../../../components/ui/Input';
import { Badge } from '../../../../components/ui/Badge';
import './DeliveryQuality.css';

/**
 * Lista los paquetes evaluados de un repartidor agrupados en bandas por
 * `totalScore` (Buenas / Regulares / Malas) con un toggle para filtrar a sólo
 * los que aún no tienen análisis IA. Cada item es clickeable y dispara
 * `onSelect(packageId)` para abrir el modal IA del paquete.
 *
 * IMPORTANTE: el identificador visible es `followingNumber`. El callback recibe
 * `onSelect(packageId, followingNumber)`: el `_id` del paquete (para el fetch del
 * detalle) y el nº de seguimiento (para mostrarlo en el modal sin exponer el _id).
 */

const VERDICT_META = {
  'ENTREGA VALIDADA': { tone: 'success', label: 'IA: Validada' },
  'ENTREGA DUDOSA': { tone: 'warning', label: 'IA: Dudosa' },
  'ENTREGA RECHAZADA': { tone: 'error', label: 'IA: Rechazada' },
  'ENTREGA INVALIDA': { tone: 'error', label: 'IA: Inválida' },
  'ENTREGA INVÁLIDA': { tone: 'error', label: 'IA: Inválida' },
};

const PENDING_VERDICT = { tone: 'neutral', label: 'IA: Pendiente' };

// Tono (clase de fondo via token) por banda de score.
const SCORE_BAND_DOT = {
  good: 'bg-status-success',
  regular: 'bg-status-warning',
  bad: 'bg-status-error',
};

const SCORE_TONE = (score) => {
  if (score < 60) return 'error';
  if (score < 75) return 'brand';
  if (score < 85) return 'warning';
  return 'success';
};

const PAGE_SIZE = 10;

const shortDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  } catch {
    return '—';
  }
};

const fmtPct = (n, total) => {
  if (!total) return '0%';
  return `${Math.round((n / total) * 100)}%`;
};

const VerdictChip = ({ verdict, confidence }) => {
  const { t } = useTranslation('deliveryquality');
  const meta = verdict ? VERDICT_META[verdict] || PENDING_VERDICT : PENDING_VERDICT;
  const tip = verdict
    ? `${verdict}${confidence ? ` · ${t('Confianza')} ${confidence}` : ''}`
    : t('El análisis IA aún no se completó para este paquete.');
  return (
    <Badge tone={meta.tone} title={tip}>
      {t(meta.label)}
    </Badge>
  );
};

const Pill = ({ ok, label, title }) => (
  <span
    className={`dq-criteria-pill ${ok === true ? 'dq-criteria-pill--ok' : ok === false ? 'dq-criteria-pill--ko' : 'dq-criteria-pill--na'}`}
    title={title}
  >
    {label}
  </span>
);

const CriteriaPills = ({ scores }) => {
  const { t } = useTranslation('deliveryquality');
  const rut = scores?.rut?.valid;
  const receiver = scores?.receiver?.valid;
  const distance = scores?.distance?.withinThreshold;
  const timeliness = scores?.timeliness?.withinGrace;
  return (
    <span className="dq-criteria-pills">
      <Pill ok={rut} label="RUT" title={t('RUT del receptor')} />
      <Pill ok={receiver} label={t('Recep')} title={t('Nombre del receptor')} />
      <Pill ok={distance} label={t('Dist')} title={t('Distancia (< 100 m)')} />
      <Pill ok={timeliness} label={t('Punt')} title={t('Puntualidad (antes 21:00)')} />
    </span>
  );
};

const PackageRow = ({ pkg, onSelect }) => {
  const { t } = useTranslation('deliveryquality');
  // Importante: el endpoint /scores/{package_id} espera el _id del PAQUETE,
  // no el _id del documento de score. _serialize() del backend renombra el
  // _id del score a `id`, así que NO lo usamos como fallback acá.
  const pkgId = pkg.packageId || pkg.package_id;
  const num = pkg.followingNumber;
  const score = pkg.totalScore ?? 0;
  return (
    <button
      type="button"
      className="dq-pkg-row"
      onClick={() => pkgId && onSelect(pkgId, num)}
      disabled={!pkgId}
    >
      <span className="dq-pkg-row__num">
        {num != null ? `#${num}` : (
          <em className="text-fg-subtle font-normal">{t('Sin nº seguimiento')}</em>
        )}
      </span>
      <span className="dq-pkg-row__date">{shortDate(pkg.deliveryDate)}</span>
      <Badge tone={SCORE_TONE(score)}>{Number.isFinite(score) ? score.toFixed(0) : '—'}</Badge>
      <VerdictChip
        verdict={pkg.scores?.imagesAi?.verdict}
        confidence={pkg.scores?.imagesAi?.confidence}
      />
      <CriteriaPills scores={pkg.scores} />
      <ChevronRight className="dq-pkg-row__chevron w-3.5 h-3.5" />
    </button>
  );
};

const Bucket = ({ band, items, total, onSelect, defaultOpen = false }) => {
  const { t } = useTranslation('deliveryquality');
  const [open, setOpen] = useState(defaultOpen);
  const [shown, setShown] = useState(PAGE_SIZE);

  const visible = items.slice(0, shown);
  const remaining = items.length - visible.length;

  const title = {
    good: t('Buenas (≥ 80)'),
    regular: t('Regulares (60–79)'),
    bad: t('Malas (< 60)'),
  }[band];

  return (
    <div className="dq-bucket">
      <button
        type="button"
        className="dq-bucket-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={`dq-bucket-header__chip ${SCORE_BAND_DOT[band]}`} />
        <span className="dq-bucket-header__title">{title}</span>
        <span className="dq-bucket-header__meta">
          {items.length} · {fmtPct(items.length, total)}
        </span>
        {open ? (
          <ChevronUp className="w-3 h-3 text-fg-subtle" />
        ) : (
          <ChevronDown className="w-3 h-3 text-fg-subtle" />
        )}
      </button>
      {open && (
        <div className="dq-bucket-body">
          {items.length === 0 ? (
            <div className="dq-bucket-empty">
              {t('Sin paquetes en esta banda para el rango.')}
            </div>
          ) : (
            <>
              {visible.map((pkg) => (
                <PackageRow
                  key={pkg.id || pkg.packageId || pkg._id || pkg.followingNumber}
                  pkg={pkg}
                  onSelect={onSelect}
                />
              ))}
              {remaining > 0 && (
                <div className="text-center px-3 py-1.5">
                  <button
                    type="button"
                    className="text-sm text-brand-600 hover:text-brand-700 hover:underline"
                    onClick={() => setShown((s) => s + PAGE_SIZE)}
                  >
                    {t('Ver más ({{n}} restantes)', { n: remaining })}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const DriverPackagesBuckets = ({ packages = [], onSelect }) => {
  const { t } = useTranslation('deliveryquality');
  const [search, setSearch] = useState('');
  const [onlyNoAi, setOnlyNoAi] = useState(false);

  const { good, regular, bad, totalShown, noAiCount } = useMemo(() => {
    const q = search.trim();
    const noAiTotal = packages.filter((p) => !p.scores?.imagesAi?.verdict).length;

    const filtered = packages.filter((p) => {
      if (q) {
        const num = p.followingNumber != null ? String(p.followingNumber) : '';
        if (!num.includes(q)) return false;
      }
      if (onlyNoAi && p.scores?.imagesAi?.verdict) return false;
      return true;
    });

    const goodArr = [];
    const regularArr = [];
    const badArr = [];
    filtered.forEach((p) => {
      const ts = p.totalScore ?? 0;
      if (ts >= 80) goodArr.push(p);
      else if (ts >= 60) regularArr.push(p);
      else badArr.push(p);
    });

    const sortByDateDesc = (a, b) => {
      const da = a.deliveryDate ? new Date(a.deliveryDate).getTime() : 0;
      const db = b.deliveryDate ? new Date(b.deliveryDate).getTime() : 0;
      return db - da;
    };
    goodArr.sort(sortByDateDesc);
    regularArr.sort(sortByDateDesc);
    badArr.sort(sortByDateDesc);

    return {
      good: goodArr,
      regular: regularArr,
      bad: badArr,
      totalShown: filtered.length,
      noAiCount: noAiTotal,
    };
  }, [packages, search, onlyNoAi]);

  if (!packages.length) {
    return (
      <div className="dq-bucket-empty rounded-md border border-dashed border-border">
        {t('Sin paquetes evaluados para este repartidor en el rango seleccionado.')}
      </div>
    );
  }

  return (
    <div>
      <div className="dq-buckets-toolbar">
        <Input
          size="sm"
          type="text"
          placeholder={t('Buscar por nº de seguimiento…')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="dq-buckets-toolbar__toggle">
          <input
            type="checkbox"
            checked={onlyNoAi}
            onChange={(e) => setOnlyNoAi(e.target.checked)}
          />
          {t('Solo sin análisis IA')}{' '}
          {noAiCount > 0 && <span className="opacity-70">({noAiCount})</span>}
        </label>
      </div>

      <Bucket band="bad" items={bad} total={totalShown} onSelect={onSelect} />
      <Bucket band="regular" items={regular} total={totalShown} onSelect={onSelect} />
      <Bucket band="good" items={good} total={totalShown} onSelect={onSelect} />
    </div>
  );
};

export default DriverPackagesBuckets;
